import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BarChart3,
  Compass,
  LayoutGrid,
  LineChart,
  MessageSquareQuote,
  Quote,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/* ----------------------------------------------------------------------- */
/*  Small building blocks used only on the landing page.                    */
/* ----------------------------------------------------------------------- */

function Wordmark({ className = "" }) {
  return (
    <Link
      to="/"
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid="landing-wordmark"
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-sm font-semibold tracking-tight text-white">
        E
      </span>
      <span className="font-display text-lg font-semibold tracking-tight text-slate-900">
        EvidenceLab
      </span>
    </Link>
  );
}

/**
 * Editorial "evidence card" — the marketing element the whole page rotates
 * around. Reuses the exact visual language of the app's finding cards
 * (confidence pill, mono citation, quote block, framework tag) so what you
 * see on the marketing page IS what you get in the product.
 */
function EvidenceCard({ payload, className = "", testId, tilt = 0 }) {
  const confidence = payload.confidence;
  const level = payload.confidenceLevel;
  return (
    <article
      data-testid={testId}
      className={`relative rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_18px_44px_-24px_rgba(15,23,42,0.35)] ${className}`}
      style={{ transform: tilt ? `rotate(${tilt}deg)` : undefined }}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="font-display text-[15px] font-semibold leading-snug tracking-tight text-slate-900">
          {payload.theme}
        </p>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${confidencePillTone(level)}`}
        >
          {confidence} {level ? `· ${level}` : ""}
        </span>
      </div>
      <div className="mt-4 border-l-2 border-amber-300/70 pl-3">
        <Quote className="mb-1 h-3.5 w-3.5 text-amber-500/80" strokeWidth={2} />
        <p className="text-[13px] italic leading-snug text-slate-700">
          {payload.quote}
        </p>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-wider text-slate-500">
        <div className="col-span-2 flex items-center gap-1 truncate">
          <span className="text-slate-400">›</span>
          <span className="truncate normal-case text-slate-600">
            {payload.sourceDomain}
          </span>
        </div>
        <div className="col-span-2 flex items-center gap-1 text-slate-400">
          <span className="tabular-nums">{payload.sourceDate}</span>
        </div>
      </dl>
      <div className="mt-4 flex items-center justify-between border-t border-dashed border-slate-100 pt-3">
        <span className="rounded-md bg-slate-900/[0.04] px-2 py-1 font-mono text-[10.5px] uppercase tracking-wider text-slate-500">
          {payload.framework}
        </span>
      </div>
    </article>
  );
}

function confidencePillTone(level) {
  const map = {
    High: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60",
    Hoch: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60",
    Magas: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60",
    Medium: "bg-amber-50 text-amber-800 ring-1 ring-amber-200/60",
    Mittel: "bg-amber-50 text-amber-800 ring-1 ring-amber-200/60",
    Közepes: "bg-amber-50 text-amber-800 ring-1 ring-amber-200/60",
    Low: "bg-rose-50 text-rose-700 ring-1 ring-rose-200/60",
  };
  return map[level] || "bg-slate-100 text-slate-700 ring-1 ring-slate-200/60";
}

/* ----------------------------------------------------------------------- */
/*  Top nav — sticky, blurs on scroll                                       */
/* ----------------------------------------------------------------------- */

function LandingNav() {
  const { t } = useTranslation("landing");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="landing-nav"
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-slate-200/70 bg-white/80 backdrop-blur"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Wordmark />
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher className="hidden sm:inline-flex" />
          <Link
            to="/login"
            data-testid="landing-nav-signin"
            className="hidden rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 sm:inline-flex"
          >
            {t("nav.signIn")}
          </Link>
          <Link
            to="/signup"
            data-testid="landing-nav-signup"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
          >
            {t("nav.getStarted")}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ----------------------------------------------------------------------- */
/*  Hero — rotating evidence card                                            */
/* ----------------------------------------------------------------------- */

function Hero() {
  const { t } = useTranslation("landing");
  const cards = useMemo(() => {
    // Pull the pillar cards — those double as marketing examples.
    const items = t("pillars.items", { returnObjects: true }) || [];
    return items.slice(0, 4).map((it) => it.card);
  }, [t]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!cards.length) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % cards.length), 6000);
    return () => clearInterval(id);
  }, [cards.length]);

  return (
    <section
      data-testid="landing-hero"
      className="relative overflow-hidden bg-editorial-paper"
    >
      <div className="absolute inset-0 bg-graph-paper opacity-[0.55] [mask-image:radial-gradient(circle_at_top,black,transparent_65%)]" />
      <div className="relative mx-auto grid max-w-6xl gap-14 px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16 lg:px-8 lg:pt-28">
        <div className="max-w-2xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-700/90">
            {t("hero.eyebrow")}
          </p>
          <h1
            data-testid="landing-hero-headline"
            className="mt-4 font-display text-4xl leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            {t("hero.headline")}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {t("hero.deck")}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/signup"
              data-testid="landing-hero-cta-primary"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-slate-800"
            >
              {t("hero.primaryCta")}
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
            <Link
              to="/login"
              data-testid="landing-hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/60 px-5 py-3 text-sm font-medium text-slate-800 transition-colors hover:bg-white"
            >
              {t("hero.secondaryCta")}
            </Link>
          </div>
        </div>
        <div
          className="relative min-h-[360px]"
          aria-label={t("hero.cardRotator.aria")}
        >
          {cards.map((c, i) => (
            <div
              key={c.theme + i}
              className={`absolute inset-x-0 top-0 transition-all duration-700 ease-out ${
                i === idx
                  ? "translate-y-0 opacity-100"
                  : i === (idx + 1) % cards.length
                  ? "translate-y-6 opacity-40 blur-[1px]"
                  : "translate-y-12 opacity-0"
              }`}
              style={{ zIndex: cards.length - ((i - idx + cards.length) % cards.length) }}
            >
              <EvidenceCard
                payload={c}
                testId={`landing-hero-card-${i}`}
                tilt={i === idx ? -1.2 : 0.8}
                className="mx-auto max-w-md"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Problem contrast                                                        */
/* ----------------------------------------------------------------------- */

function ProblemSection() {
  const { t } = useTranslation("landing");
  const evidence = t("problem.evidenceLab", { returnObjects: true });
  const chat = t("problem.chatGpt", { returnObjects: true });
  const components = Array.isArray(evidence.components) ? evidence.components : [];
  return (
    <section
      data-testid="landing-problem"
      className="border-y border-slate-200/70 bg-white"
    >
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-700/90">
          {t("problem.kicker")}
        </p>
        <h2 className="mt-3 max-w-3xl font-display text-3xl leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
          {t("problem.title")}
        </h2>

        <div className="mt-14 grid gap-8 md:grid-cols-2">
          {/* Generic AI panel */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-6">
            <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
              {chat.brand}
            </p>
            <blockquote className="mt-4 space-y-3">
              <p className="font-display text-lg italic leading-snug text-slate-700 sm:text-xl">
                “{chat.quote1}”
              </p>
              <p className="font-display text-lg italic leading-snug text-slate-700 sm:text-xl">
                “{chat.quote2}”
              </p>
            </blockquote>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-wider text-slate-400">
              {chat.meta}
            </p>
          </div>
          {/* EvidenceLab panel */}
          <div className="relative rounded-2xl border border-slate-900/90 bg-slate-900 p-6 text-white">
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber-300/90">
              {evidence.brand}
            </p>
            <p className="mt-4 font-display text-lg leading-snug text-white sm:text-xl">
              {evidence.claim}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/10 pt-5 font-mono text-[11px] uppercase tracking-wider text-white/60">
              <div>
                <p>{evidence.sourceLabel}</p>
                <p className="normal-case text-white/90">{evidence.sourceDomain}</p>
              </div>
              <div>
                <p>{evidence.confidenceLabel}</p>
                <p className="tabular-nums normal-case text-emerald-300">
                  {evidence.confidenceValue}
                </p>
              </div>
              <div className="col-span-2 tabular-nums text-white/50">
                {evidence.sourceDate}
              </div>
            </div>
            <ul className="mt-5 space-y-1.5">
              {components.map((c) => (
                <li
                  key={c.label}
                  className="flex items-center justify-between font-mono text-[11px] text-white/70"
                >
                  <span className="uppercase tracking-wider">{c.label}</span>
                  <span className="normal-case text-white">{c.value}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 border-t border-white/10 pt-4">
              <span className="rounded-md bg-white/10 px-2 py-1 font-mono text-[10.5px] uppercase tracking-wider text-white">
                {evidence.frameworkTag}
              </span>
            </div>
          </div>
        </div>

        <p className="mt-14 max-w-2xl font-display text-2xl italic leading-snug tracking-tight text-slate-900">
          {t("problem.closer")}
        </p>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Pillars                                                                  */
/* ----------------------------------------------------------------------- */

function PillarsSection() {
  const { t } = useTranslation("landing");
  const items = t("pillars.items", { returnObjects: true }) || [];

  return (
    <section data-testid="landing-pillars" className="bg-editorial-paper">
      <div className="absolute inset-x-0 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" />
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-700/90">
          {t("pillars.kicker")}
        </p>
        <h2 className="mt-3 max-w-3xl font-display text-3xl leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
          {t("pillars.sectionTitle")}
        </h2>
        <div className="mt-16 space-y-20">
          {items.map((item, i) => (
            <PillarRow key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PillarRow({ item, index }) {
  const flip = index % 2 === 1;
  return (
    <article
      data-testid={`landing-pillar-${index}`}
      className={`grid gap-10 lg:grid-cols-2 lg:items-center ${
        flip ? "" : ""
      }`}
    >
      <div className={`max-w-xl ${flip ? "lg:order-2" : ""}`}>
        <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400 tabular-nums">
          0{index + 1}
        </p>
        <h3 className="mt-3 font-display text-2xl leading-snug tracking-tight text-slate-900 sm:text-3xl">
          {item.title}
        </h3>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
          {item.body}
        </p>
      </div>
      <div className={`relative ${flip ? "lg:order-1" : ""}`}>
        {/* Decorative back card + focal card for depth */}
        <div className="pointer-events-none absolute inset-0 mx-auto max-w-md translate-x-3 translate-y-3 rounded-2xl border border-slate-200/70 bg-white/60" />
        <EvidenceCard
          payload={item.card}
          className="relative mx-auto max-w-md"
          testId={`landing-pillar-${index}-card`}
          tilt={flip ? 1.2 : -1.2}
        />
      </div>
    </article>
  );
}

/* ----------------------------------------------------------------------- */
/*  Framework grid                                                          */
/* ----------------------------------------------------------------------- */

const FRAMEWORK_ICONS = [Compass, BarChart3, MessageSquareQuote, LineChart, LayoutGrid];

function FrameworksSection() {
  const { t } = useTranslation("landing");
  const items = t("frameworks.items", { returnObjects: true }) || [];
  const cols = t("frameworks.columns", { returnObjects: true }) || {};
  return (
    <section
      data-testid="landing-frameworks"
      className="border-y border-slate-200/70 bg-white"
    >
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-700/90">
          {t("frameworks.kicker")}
        </p>
        <h2 className="mt-3 max-w-3xl font-display text-3xl leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
          {t("frameworks.title")}
        </h2>
        <div className="mt-12 overflow-hidden rounded-2xl border border-slate-200">
          <div className="hidden grid-cols-[auto_1.4fr_1.4fr_2fr] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500 md:grid">
            <span />
            <span>{cols.type}</span>
            <span>{cols.framework}</span>
            <span>{cols.description}</span>
          </div>
          <ul>
            {items.map((it, i) => {
              const Icon = FRAMEWORK_ICONS[i] || Compass;
              return (
                <li
                  key={i}
                  data-testid={`landing-framework-${i}`}
                  className="grid gap-2 border-b border-slate-100 px-5 py-5 last:border-b-0 md:grid-cols-[auto_1.4fr_1.4fr_2fr] md:items-center md:gap-4"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900/[0.04] text-slate-700">
                    <Icon className="h-4 w-4" strokeWidth={1.6} />
                  </span>
                  <span className="font-display text-lg tracking-tight text-slate-900">
                    {it.type}
                  </span>
                  <span className="font-mono text-[12px] uppercase tracking-wider text-amber-800/90">
                    {it.framework}
                  </span>
                  <span className="text-sm leading-relaxed text-slate-600">
                    {it.description}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  How it works — 4 steps                                                  */
/* ----------------------------------------------------------------------- */

function HowSection() {
  const { t } = useTranslation("landing");
  const steps = t("how.steps", { returnObjects: true }) || [];
  return (
    <section data-testid="landing-how" className="bg-editorial-paper">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-700/90">
          {t("how.kicker")}
        </p>
        <h2 className="mt-3 max-w-3xl font-display text-3xl leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
          {t("how.title")}
        </h2>

        <ol className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <li
              key={i}
              data-testid={`landing-how-step-${i}`}
              className="relative"
            >
              <div className="flex items-baseline gap-3 border-t-2 border-slate-900/80 pt-4">
                <span className="font-display text-3xl font-semibold text-slate-900 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
                  Step
                </span>
              </div>
              <h3 className="mt-4 font-display text-xl leading-snug tracking-tight text-slate-900">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Closing CTA                                                             */
/* ----------------------------------------------------------------------- */

function ClosingSection() {
  const { t } = useTranslation("landing");
  return (
    <section data-testid="landing-closing" className="relative overflow-hidden bg-slate-900 text-white">
      <div className="absolute inset-0 bg-graph-paper-dark opacity-[0.16]" />
      <div className="relative mx-auto max-w-4xl px-4 py-24 text-center sm:px-6 sm:py-28 lg:px-8">
        <h2 className="font-display text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl">
          {t("closing.title")}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-white/70 sm:text-lg">
          {t("closing.deck")}
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/signup"
            data-testid="landing-closing-cta-primary"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-100"
          >
            {t("closing.primaryCta")}
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
          <Link
            to="/login"
            data-testid="landing-closing-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/5"
          >
            {t("closing.secondaryCta")}
          </Link>
        </div>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-wider text-white/40">
          {t("closing.finePrint")}
        </p>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------------- */
/*  Footer                                                                  */
/* ----------------------------------------------------------------------- */

function Footer() {
  const { t } = useTranslation("landing");
  return (
    <footer
      data-testid="landing-footer"
      className="border-t border-slate-200 bg-white"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-10 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <div>
          <Wordmark />
          <p className="mt-2 text-sm text-slate-500">{t("footer.tagline")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            to="/legal/privacy"
            data-testid="landing-footer-privacy"
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            {t("footer.links.privacy")}
          </Link>
          <Link
            to="/legal/terms"
            data-testid="landing-footer-terms"
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            {t("footer.links.terms")}
          </Link>
          <LanguageSwitcher />
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8">
        <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
          {t("footer.copyright")}
        </p>
      </div>
    </footer>
  );
}

/* ----------------------------------------------------------------------- */
/*  Landing entry                                                            */
/* ----------------------------------------------------------------------- */

export default function Landing() {
  const { status } = useAuth();
  const { t, i18n } = useTranslation("landing");

  // Head-tag effects — we don't own <head>, but keep the tab title in sync
  // with the marketing meta so a shared preview URL feels branded.
  const titleRef = useRef(null);
  useEffect(() => {
    const previous = document.title;
    document.title = t("meta.title");
    // Update meta description too if it exists.
    const desc = document.querySelector('meta[name="description"]');
    const originalDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", t("meta.description"));
    titleRef.current = { previous, originalDesc };
    return () => {
      if (titleRef.current) {
        document.title = titleRef.current.previous;
        if (desc && titleRef.current.originalDesc)
          desc.setAttribute("content", titleRef.current.originalDesc);
      }
    };
  }, [i18n.language, t]);

  if (status === "loading") {
    return (
      <div
        data-testid="landing-loading"
        className="flex min-h-screen items-center justify-center bg-editorial-paper text-sm text-slate-500"
      >
        …
      </div>
    );
  }

  if (status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen bg-editorial-paper text-slate-900"
      lang={(i18n.resolvedLanguage || i18n.language || "en").slice(0, 2)}
    >
      <LandingNav />
      <main>
        <Hero />
        <ProblemSection />
        <PillarsSection />
        <FrameworksSection />
        <HowSection />
        <ClosingSection />
      </main>
      <Footer />
    </div>
  );
}
